import type { ElementType } from 'react';
import React, { Fragment, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { ListItemTextProps } from '@mui/material';
import { Box, Divider, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { Link } from 'react-router-dom';
import { useRouteMatch } from 'react-router';

import * as Icons from '@mui/icons-material';

import { MenuToggleIconButton, StyledDrawer, StyledList, StyledListItem, StyledListItemIcon } from './styles.ts';

import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import ScenarioSection from './ScenarioSection/scenarioSection.tsx';
import NavigationSection from './NavigationSection.tsx';
import PreviewChip from './PreviewChip.tsx';

import * as ScenarioActions from '../../actions/scenarios/scenarioAction.ts';
import AppRoutes from '../../utils/routes.ts';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { isUserAnyAdminSelector, isUserOwnerSelector, isUserSuperAdminSelector } from 'app/selectors/userSelectors.ts';
import { GuiStateActions } from 'app/actions/guiState/guiStateAction.ts';
import greyLogo from '/icons/logo.svg?url';
import greyLogoMinimal from '/icons/logo-minimal.svg?url';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useLogout } from 'app/hooks/useLogout.ts';
import StatisticsSection from './StatisticsSection/StatisticsSection.tsx';
import SimulationIcon from 'app/components/utils/icons/simulationIcon/simulationIcon.tsx';

export default function NavigationDrawer(): JSX.Element {
  const [translate] = useTranslation(I18nNamespace.Common);
  const theme = useTheme();
  const logout = useLogout();
  const dispatch = useDispatch();
  const sideBarOpenState = useSelector(GuiStateSelectors.sideBarStateSelector);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const isUserSuperAdmin = useSelector(isUserSuperAdminSelector);
  const isUserOwner = useSelector(isUserOwnerSelector);
  const isUserAnyAdmin = useSelector(isUserAnyAdminSelector);

  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const hasSkllsAccess = !!(currentUser && currentUser.hasSklls === true);
  const hasInterpreterAccess = !!(currentUser && currentUser.hasInterpreter === true);
  const hasExercisesAccess = !!(currentUser && currentUser.hasExercises === true);
  const scenarios = useSelector(ScenarioSelectors.scenariosSelector);
  const getScenarios = useCallback(() => dispatch(ScenarioActions.getScenarios()), [dispatch]);

  useEffect(() => {
    if (currentUser && scenarios.length === 0) {
      getScenarios();
    }
  }, [getScenarios, currentUser, scenarios]);

  const toggleDrawer = () => {
    dispatch(GuiStateActions.sideMenuOpen(!sideBarOpenState));
  };

  const usersRouteMatch = useRouteMatch({ path: AppRoutes.UserManagement, strict: false });
  const groupManagementRouteMatch = useRouteMatch({ path: AppRoutes.GroupManagement, strict: false });
  const dashboardRouteMatch = useRouteMatch({ path: AppRoutes.Dashboard, strict: false });
  const conversationsRouteMatch = useRouteMatch({ path: AppRoutes.Conversations, strict: false });
  const courseManagementRouteMatch = useRouteMatch({ path: AppRoutes.CourseManagement, strict: false });
  const myCoursesRouteMatch = useRouteMatch({ path: AppRoutes.MyCourses, strict: false });
  const globalAssetsLibraryRouteMatch = useRouteMatch({ path: AppRoutes.GlobalAssetsLibrary, strict: false });
  const callsRouteMatch = useRouteMatch({ path: AppRoutes.CallManagement, strict: false });
  const userGuidesRouteMatch = useRouteMatch({ path: AppRoutes.UserGuides, strict: false });
  const translatorRouteMatch = useRouteMatch({ path: AppRoutes.Translator, strict: false });
  const exercisesRouteMatch = useRouteMatch({ path: AppRoutes.ExercisesManager, strict: false });
  const aiSimulationsRouteMatch = useRouteMatch({ path: AppRoutes.AiSimulationsManager, strict: false });
  const failedJobsRouteMatch = useRouteMatch({ path: AppRoutes.RecentJobs, strict: false });
  const companyManagementRouteMatch = useRouteMatch({ path: AppRoutes.CompaniesManagement, strict: false });
  const evaluationProtocolsRouteMatch = useRouteMatch({ path: AppRoutes.EvaluationProtocolManager, strict: false });
  const personasRouteMatch = useRouteMatch({ path: AppRoutes.PersonasManager, strict: false });
  const huginLabRouteMatch = useRouteMatch({ path: AppRoutes.HuginLab, strict: false });

  const reportsRouteMatch = useRouteMatch({
    path: AppRoutes.Reports,
    strict: false
  });

  const DrawerListItemText = (props: ListItemTextProps) => {
    if (!sideBarOpenState) {
      return null;
    }
    return <ListItemText {...props} />;
  };

  const showInterpreter = hasInterpreterAccess || ((isUserSuperAdmin || isUserOwner) && hasSkllsAccess);

  const dashboardItem = hasSkllsAccess && (
    <StyledListItem
      selected={!!dashboardRouteMatch}
      data-cy="dashboard"
      component={Link as ElementType}
      to={AppRoutes.Dashboard}
    >
      <StyledListItemIcon>
        <Icons.Dashboard />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.dashboard))} />
    </StyledListItem>
  );

  // Everyone (members included) gets the scenarios section so the recent list shows
  // in the drawer. The "New scenario" button inside it stays admin-only.
  const scenariosItem = hasSkllsAccess && <ScenarioSection />;

  const conversationsItem = hasSkllsAccess && (
    <StyledListItem
      selected={!!conversationsRouteMatch}
      data-cy="conversations"
      component={Link as ElementType}
      to={AppRoutes.Conversations}
    >
      <StyledListItemIcon>
        <Icons.History />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.conversations))} />
    </StyledListItem>
  );

  const scheduledCallsItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.CallManagement} selected={!!callsRouteMatch}>
      <StyledListItemIcon>
        <Icons.Phone />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.scheduledCalls))} />
    </StyledListItem>
  );

  const assistantItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.HuginLab}
      selected={!!huginLabRouteMatch}
      data-cy="hugin-lab"
    >
      <StyledListItemIcon>
        <Icons.AutoAwesomeOutlined />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.huginLab))} />
      {sideBarOpenState && <PreviewChip />}
    </StyledListItem>
  );

  const exercisesItem = isUserAnyAdmin && hasExercisesAccess && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.ExercisesManager} selected={!!exercisesRouteMatch}>
      <StyledListItemIcon>
        <Icons.EventNote />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.exercises))} />
      {sideBarOpenState && <PreviewChip />}
    </StyledListItem>
  );

  // SuperAdmin-only for now — the early Simulation feature is not exposed to
  // organization admins/owners (enforced backend-side too).
  const aiSimulationsItem = isUserSuperAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.AiSimulationsManager}
      selected={!!aiSimulationsRouteMatch}
      data-cy="ai-simulations"
    >
      <StyledListItemIcon>
        <SimulationIcon />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.aiSimulations))} />
    </StyledListItem>
  );

  const interpreterItem = showInterpreter && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.Translator} selected={!!translatorRouteMatch}>
      <StyledListItemIcon>
        <Icons.Translate />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.translator))} />
      {sideBarOpenState && <PreviewChip />}
    </StyledListItem>
  );

  const coursesItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.CourseManagement}
      selected={!!courseManagementRouteMatch}
    >
      <StyledListItemIcon>
        <Icons.School />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.courseManagement))} />
    </StyledListItem>
  );

  const courseProgressItem = hasSkllsAccess && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.MyCourses} selected={!!myCoursesRouteMatch}>
      <StyledListItemIcon>
        <Icons.LocalLibrary />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.courseStatus))} />
    </StyledListItem>
  );

  const personasItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.PersonasManager} selected={!!personasRouteMatch}>
      <StyledListItemIcon>
        <Icons.Person />
      </StyledListItemIcon>
      <DrawerListItemText primary="Personas" />
    </StyledListItem>
  );

  const evaluationProtocolsItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.EvaluationProtocolManager}
      selected={!!evaluationProtocolsRouteMatch}
    >
      <StyledListItemIcon>
        <Icons.Assessment />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.evaluationProtocols))} />
    </StyledListItem>
  );

  const globalIntentsItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.GlobalAssetsLibrary}
      selected={!!globalAssetsLibraryRouteMatch}
    >
      <StyledListItemIcon>
        <Icons.LibraryAddCheck />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.globalAssetsLibrary))} />
    </StyledListItem>
  );

  const usersItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem selected={!!usersRouteMatch} component={Link as ElementType} to={AppRoutes.UserManagement}>
      <StyledListItemIcon>
        <Icons.SupervisorAccount />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.userManagement))} />
    </StyledListItem>
  );

  const organizationItem = isUserSuperAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.CompaniesManagement}
      selected={!!companyManagementRouteMatch}
    >
      <StyledListItemIcon>
        <BusinessIcon />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.companyManagement))} />
    </StyledListItem>
  );

  const groupsItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.GroupManagement}
      selected={!!groupManagementRouteMatch}
    >
      <StyledListItemIcon>
        <Icons.SupervisedUserCircle />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.groupManagement))} />
    </StyledListItem>
  );

  const reportsItem = isUserAnyAdmin && hasSkllsAccess && (
    <StyledListItem
      component={Link as ElementType}
      to={AppRoutes.Reports}
      selected={!!reportsRouteMatch}
      data-cy="reports"
    >
      <StyledListItemIcon>
        <Icons.InsertChartOutlined />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.reports))} />
      {sideBarOpenState && <PreviewChip />}
    </StyledListItem>
  );

  const recentJobsItem = isUserSuperAdmin && hasSkllsAccess && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.RecentJobs} selected={!!failedJobsRouteMatch}>
      <StyledListItemIcon>
        <Icons.ErrorOutline />
      </StyledListItemIcon>
      <DrawerListItemText
        primary={translate(
          nameof.full<I18nCommonNs>((l) => l.navigation.recentJobs),
          'Recent Jobs'
        )}
      />
    </StyledListItem>
  );

  const helpItem = hasSkllsAccess && (
    <StyledListItem component={Link as ElementType} to={AppRoutes.UserGuides} selected={!!userGuidesRouteMatch}>
      <StyledListItemIcon>
        <Icons.Help />
      </StyledListItemIcon>
      <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.userGuides))} />
    </StyledListItem>
  );

  return (
    <Fragment>
      <StyledDrawer
        variant="permanent"
        anchor="left"
        id="sideBarState"
        open={sideBarOpenState}
        data-cy={sideBarOpenState}
      >
        <Box sx={{ position: 'relative' }}>
          <MenuToggleIconButton
            onClick={toggleDrawer}
            data-cy="sidebarWrapper"
            size="small"
            sx={{ right: sideBarOpenState ? -20 : -40 }}
            aria-label={
              sideBarOpenState
                ? translate(nameof.full<I18nCommonNs>((l) => l.navigation.close))
                : translate(nameof.full<I18nCommonNs>((l) => l.navigation.open))
            }
          >
            {!sideBarOpenState ? <ChevronRight /> : <ChevronLeft />}
          </MenuToggleIconButton>
        </Box>

        <Box
          component={Link}
          to={AppRoutes.Dashboard}
          sx={{ margin: isSmallScreen ? '19px 40px 0 72px' : '24px 40px 0 36px' }}
        >
          <img
            src={sideBarOpenState || isSmallScreen ? greyLogo : greyLogoMinimal}
            alt="Sklls"
            className="h-8.5 w-auto"
          />
        </Box>

        <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
          <StyledList
            subheader={<li />}
            sx={{
              alignItems: sideBarOpenState ? 'normal' : 'flex-start',
              paddingRight: sideBarOpenState ? theme.spacing(2) : 0
            }}
          >
            {dashboardItem}
            {scenariosItem}
            {conversationsItem}
            {scheduledCallsItem}
            {assistantItem}
            {exercisesItem}
            {aiSimulationsItem}
            {interpreterItem}

            <NavigationSection
              id="content"
              label={translate(nameof.full<I18nCommonNs>((l) => l.navigation.sections.content))}
              defaultExpanded={false}
              sideBarOpen={sideBarOpenState}
            >
              {personasItem}
              {evaluationProtocolsItem}
              {globalIntentsItem}
            </NavigationSection>

            <NavigationSection
              id="courses"
              label={translate(nameof.full<I18nCommonNs>((l) => l.navigation.sections.courses))}
              defaultExpanded={false}
              sideBarOpen={sideBarOpenState}
            >
              {coursesItem}
              {courseProgressItem}
            </NavigationSection>

            <NavigationSection
              id="admin"
              label={translate(nameof.full<I18nCommonNs>((l) => l.navigation.sections.admin))}
              defaultExpanded={false}
              sideBarOpen={sideBarOpenState}
            >
              {usersItem}
              {organizationItem}
              {groupsItem}
              {reportsItem}
              {recentJobsItem}
              {isUserSuperAdmin && hasSkllsAccess && <StatisticsSection />}
            </NavigationSection>
          </StyledList>
        </Box>

        <Box sx={{ paddingInline: theme.spacing(2) }}>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {helpItem}
            <StyledListItem
              component="button"
              aria-label={translate(nameof.full<I18nCommonNs>((l) => l.signOut))}
              sx={{ width: '100%' }}
              onClick={logout}
            >
              <StyledListItemIcon>
                <Icons.Logout />
              </StyledListItemIcon>
              <DrawerListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.signOut))} />
            </StyledListItem>
          </Box>
        </Box>
      </StyledDrawer>
    </Fragment>
  );
}
